import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Gift } from 'lucide-react';
import LoyaltyCard, { cardTheme } from '../components/loyalty/LoyaltyCard';
import axios from 'axios';
import { safeCapture } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUBMIT_TIMEOUT_MS = 15000;

const JoinPage = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [canRetry, setCanRetry] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [consent, setConsent] = useState(false);
  // R1 — AbortController so a stuck POST can be cancelled and retried cleanly
  const abortRef = useRef(null);

  const fetchCard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/public/card/${cardId}`);
      setCard(data);
    } catch {
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  useEffect(() => { fetchCard(); }, [fetchCard]);

  // Funnel analytics — fire once per page view when the card payload is
  // available (so we have card_id + card_type in the event props).
  // Anonymous: no PII attached.
  useEffect(() => {
    if (!card?.id) return;
    safeCapture('join_page_viewed', {
      card_id: card.id,
      card_type: card.card_type,
      is_android: /android/i.test(navigator.userAgent),
    });
  }, [card?.id, card?.card_type]);

  const submitJoin = useCallback(async () => {
    setSubmitting(true);
    setError('');
    setCanRetry(false);
    // R1 — Abort previous in-flight request if user clicks retry
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await axios.post(
        `${API_URL}/api/public/join/${cardId}`,
        { name: form.name.trim(), email: form.email.trim().toLowerCase(), gdpr_consent: consent },
        { timeout: SUBMIT_TIMEOUT_MS, signal: ctrl.signal }
      );
      const memberCode = res.data.member_code;
      if (memberCode) {
        navigate(`/wallet/${memberCode}?new=true`);
      } else {
        navigate(`/wallet/${res.data.customer_id}?new=true`);
      }
    } catch (err) {
      // R1 — Distinguish timeout/abort/network errors from validation errors
      const isTimeout = err?.code === 'ECONNABORTED'
        || err?.code === 'ERR_CANCELED'
        || err?.name === 'CanceledError'
        || /timeout/i.test(err?.message || '');
      const isNetwork = err?.code === 'ERR_NETWORK' || !err?.response;
      const data = err.response?.data;
      const detail = data?.detail;
      const errors = data?.errors;
      let msg;
      if (isTimeout) {
        msg = 'La création de votre carte prend plus de temps que prévu. Cliquez sur Réessayer.';
        setCanRetry(true);
      } else if (isNetwork) {
        msg = 'Connexion instable. Cliquez sur Réessayer.';
        setCanRetry(true);
      } else if (Array.isArray(errors) && errors.length > 0) {
        msg = errors[0]?.message || 'Données invalides';
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail[0]?.msg || detail[0]?.message || 'Données invalides';
      } else if (detail && typeof detail === 'object') {
        msg = detail.message || JSON.stringify(detail);
      } else {
        msg = 'Impossible de rejoindre. Veuillez réessayer.';
        setCanRetry(true);
      }
      setError(msg);
    } finally {
      setSubmitting(false);
      abortRef.current = null;
    }
  }, [cardId, form.name, form.email, consent, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) { setError('Le prénom doit contenir au moins 2 caractères.'); setCanRetry(false); return; }
    if (!form.email.trim()) { setError('Indiquez votre email pour recevoir la carte.'); setCanRetry(false); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError('Cet email ne semble pas complet (ex. prenom@gmail.com).'); setCanRetry(false); return; }
    // Funnel analytics — local validation passed, the form is about to be
    // POSTed. Anonymous: no PII (name/email) attached.
    safeCapture('join_form_submitted', {
      card_id: card?.id,
      card_type: card?.card_type,
    });
    await submitJoin();
  };

  const handleRetry = async () => {
    await submitJoin();
  };

  // Cleanup on unmount: abort any pending request to avoid memory leak / setState on unmounted
  useEffect(() => () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-[#EEF0F2] px-4 pt-6">
        <div className="max-w-md mx-auto space-y-4" aria-busy="true">
          <div className="h-[230px] rounded-[22px] bg-[#DCE0E5] animate-pulse" />
          <div className="h-[260px] rounded-[22px] bg-white/70 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-[100dvh] bg-[#EEF0F2] flex items-center px-6">
        <div className="max-w-md mx-auto">
          <h1 className="text-[26px] font-bold text-[#15171A] mb-3">Cette carte n’est plus disponible</h1>
          <p className="text-[17px] leading-relaxed text-[#4A515C]" data-testid="join-error">
            Le QR que vous avez scanné ne correspond à aucune carte active. Demandez au commerce son nouveau QR.
          </p>
        </div>
      </div>
    );
  }

  const isStamps = card.card_type === 'stamps';
  const total = isStamps ? card.total_stamps : card.reward_threshold;
  const perEuro = card.points_per_euro ?? card.points_per_visit ?? 1;
  const theme = cardTheme(card);
  const showMerchant = card.merchant_name
    && card.merchant_name.trim().toLowerCase() !== (card.name || '').trim().toLowerCase();

  return (
    <div className="min-h-[100dvh] bg-[#EEF0F2]" data-testid="join-page-card">
      <div className="max-w-md mx-auto px-4 pt-5 pb-8">
        {showMerchant && (
          <p className="text-[15px] font-semibold text-[#4A515C] mb-3 px-1" data-testid="join-merchant-name">{card.merchant_name}</p>
        )}

        {/* The card the customer is about to get, with its reward */}
        <LoyaltyCard
          card={card}
          balance={0}
          hideProgress
          title={card.reward_description || 'Votre carte de fidélité'}
          subtitle={isStamps
            ? `offert au bout de ${total} tampons`
            : `offert à ${total} points · ${perEuro} point${perEuro > 1 ? 's' : ''} par euro`}
        />
        <span className="sr-only" data-testid="join-card-name">{card.name}</span>

        <form onSubmit={handleSubmit} className="mt-5 bg-white rounded-[22px] p-5 space-y-5 shadow-[0_1px_0_rgba(21,23,26,0.04)]" noValidate>
          <h2 className="text-[21px] font-bold text-[#15171A] leading-tight">Recevez votre carte sur votre téléphone</h2>

          {error && (
            <div className="p-3 bg-[#FDECEA] rounded-xl text-[#8A1F17] text-[15px] flex items-center justify-between gap-3" role="alert" data-testid="join-error">
              <span className="flex-1">{error}</span>
              {canRetry && !submitting && (
                <button
                  type="button"
                  onClick={handleRetry}
                  className="bg-[#15171A] text-white text-sm font-semibold px-3 py-2 rounded-lg flex-shrink-0"
                  data-testid="join-retry-btn"
                >
                  Réessayer
                </button>
              )}
            </div>
          )}

          <label className="block">
            <span className="block text-[16px] font-semibold text-[#15171A] mb-2">Prénom</span>
            <input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoComplete="given-name"
              autoCapitalize="words"
              enterKeyHint="next"
              required
              className="w-full h-14 rounded-xl border border-[#D5D9DF] px-4 text-[18px] text-[#15171A] outline-none focus:border-[#15171A] focus:ring-2 focus:ring-[#15171A]/10"
              data-testid="join-name-input"
            />
          </label>

          <label className="block">
            <span className="block text-[16px] font-semibold text-[#15171A] mb-2">Email</span>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="go"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="w-full h-14 rounded-xl border border-[#D5D9DF] px-4 text-[18px] text-[#15171A] outline-none focus:border-[#15171A] focus:ring-2 focus:ring-[#15171A]/10"
              data-testid="join-email-input"
            />
            <span className="block text-[14px] text-[#5D6470] mt-2">Pour vous prévenir quand une récompense vous attend.</span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="join-consent-label">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-6 h-6 shrink-0 rounded-md border-[#B9C0C9]"
              style={{ accentColor: theme.bg }}
              data-testid="join-consent-checkbox"
            />
            <span className="text-[14px] text-[#4A515C] leading-relaxed">
              J’accepte que {card.merchant_name || 'le commerce'} utilise mon prénom et mon email pour ma carte de fidélité
              (<a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="underline">confidentialité</a>).
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting || !consent}
            className="w-full h-16 rounded-2xl text-[19px] font-semibold flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:opacity-50 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#15171A]"
            style={{ background: theme.bg, color: theme.fg }}
            data-testid="join-submit-btn"
          >
            {submitting ? <Loader2 className="w-6 h-6 animate-spin" /> : <Gift className="w-6 h-6" />}
            {submitting ? 'Création de votre carte…' : 'Obtenir ma carte'}
          </button>
          {submitting && (
            <p className="text-center text-[14px] text-[#5D6470] -mt-2" data-testid="join-submit-progress">
              Quelques secondes…
            </p>
          )}
          {!consent && !submitting && (
            <p className="text-center text-[14px] text-[#5D6470] -mt-2">Cochez la case ci-dessus pour continuer.</p>
          )}
        </form>

        <p className="text-center text-[14px] text-[#5D6470] mt-5 px-4 leading-relaxed">
          Gratuit et sans application : la carte s’ajoute à Apple Wallet ou Google Wallet.
        </p>
      </div>
    </div>
  );
};

export default JoinPage;
